import { ApiError } from "./api";

/**
 * Copy for a workload that vanished while its page was open.
 *
 * A create that died half-way leaves a row the API reconciles and removes
 * within about fifteen minutes; the page polling that row sees a 404 where a
 * spinner was. That is the one 404 with a story worth telling: "not found"
 * reads as a broken link, when the truth is that the create failed and can be
 * retried.
 *
 * It is recognised by what the page last knew. A database still `creating`
 * can only be one that never finished — the create writes `running` the
 * moment the engine accepts the deploy. An app is `draft` both before its
 * first deploy and when it never provisioned, so for apps the tell is the
 * status the engine never confirmed: `stale` is what the API reports when it
 * has no remote id to ask about. Anything else that disappears mid-view — an
 * app deleted from another tab — keeps the API's own wording.
 */
export function vanishedWhileCreating(
  error: unknown,
  kind: "app" | "database",
  last: { value: string; stale: boolean } | undefined,
): "vanished" | null {
  if (!(error instanceof ApiError) || error.status !== 404 || !last) return null;

  const unfinished =
    kind === "database" ? last.value === "creating" : last.value === "draft" && last.stale;
  if (!unfinished) return null;

  /* The sentence names the thing, so the thing’s own namespace carries it:
     `app.vanished` and `database.vanished`. */
  return "vanished";
}
